import React, { useEffect, useState } from 'react';
import { BookmarkPlus, FolderOpen, Trash2, Save, X } from 'lucide-react';
import { useT } from '../i18n/LanguageContext';

const STORAGE_KEY = 'hoc.presets';

function loadPresets() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) || [] : [];
  } catch (e) { return []; }
}
function savePresets(list) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch (e) { /* ignore */ }
}

export default function PresetProfiles({ snapshot, onLoad }) {
  const { t } = useT();
  const [presets, setPresets] = useState(() => loadPresets());
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');

  useEffect(() => { savePresets(presets); }, [presets]);

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const data = snapshot();
    const existing = presets.findIndex((p) => p.name === trimmed);
    const entry = { name: trimmed, savedAt: Date.now(), data };
    if (existing >= 0) {
      const next = [...presets];
      next[existing] = entry;
      setPresets(next);
    } else {
      setPresets([entry, ...presets].slice(0, 20));
    }
    setName('');
  };

  const handleDelete = (presetName) => {
    setPresets(presets.filter((p) => p.name !== presetName));
  };

  const handleLoad = (preset) => {
    onLoad(preset.data);
    setOpen(false);
  };

  return (
    <section className="rounded-xl bg-white dark:bg-[#0f3024] border border-neutral-200 dark:border-[#1a4030] shadow-[0_6px_18px_rgba(0,0,0,0.05)] dark:shadow-[0_6px_18px_rgba(0,0,0,0.4)] overflow-hidden">
      <div className="h-1 bg-gradient-to-r from-[#c92a2a] via-[#e8b84d] to-[#c92a2a]" />
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-3 border-b border-neutral-100 dark:border-[#1a4030] bg-neutral-50/60 dark:bg-[#0a2419]/60 flex items-center justify-between hover:bg-neutral-100/60 dark:hover:bg-[#0a2419]/80 transition-colors"
        aria-expanded={open}
      >
        <h2 className="font-serif font-bold text-neutral-900 dark:text-[#f5e6b8] flex items-center gap-2">
          <BookmarkPlus className="w-4 h-4 text-[#c92a2a] dark:text-[#e8b84d]" />
          {t('presets.section')}
        </h2>
        <span className="text-[11px] font-semibold text-neutral-500 dark:text-[#8da798]">
          {presets.length} {t('presets.saved')}
        </span>
      </button>

      {open && (
        <div className="px-5 py-4 space-y-3">
          {/* Save new */}
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
              placeholder={t('presets.placeholder')}
              className="flex-1 min-w-0 px-3 py-2 rounded-md border-2 border-neutral-200 dark:border-[#1a4030] bg-white dark:bg-[#0a2419] text-neutral-900 dark:text-[#f5e6b8] placeholder-neutral-400 dark:placeholder-[#6a8a76] focus:outline-none focus:border-[#c92a2a]"
            />
            <button
              type="button"
              onClick={handleSave}
              disabled={!name.trim()}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#c92a2a] text-white font-semibold text-sm hover:bg-[#a51f22] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" /> {t('presets.save')}
            </button>
          </div>

          {/* List */}
          {presets.length === 0 ? (
            <p className="text-[12px] text-neutral-500 dark:text-[#8da798] italic text-center py-2">
              {t('presets.empty')}
            </p>
          ) : (
            <ul className="divide-y divide-dashed divide-neutral-200 dark:divide-[#1a4030]">
              {presets.map((p) => (
                <li key={p.name} className="flex items-center gap-2 py-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-neutral-900 dark:text-[#f5e6b8] truncate">{p.name}</div>
                    <div className="text-[11px] text-neutral-500 dark:text-[#8da798]">
                      {new Date(p.savedAt).toLocaleDateString()} · {t(`mode.${p.data.mode === 'strong-first' ? 'royal' : 'even'}`)}
                      {' · '}
                      {(p.data.infPer || 0).toLocaleString()} inf
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleLoad(p)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border-2 border-[#c92a2a] text-[#c92a2a] dark:border-[#e8b84d] dark:text-[#e8b84d] text-xs font-semibold hover:bg-[#fff5f5] dark:hover:bg-[#1a4030]"
                    title={t('presets.load')}
                  >
                    <FolderOpen className="w-3.5 h-3.5" /> {t('presets.load')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(p.name)}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md border-2 border-neutral-200 dark:border-[#1a4030] text-neutral-500 dark:text-[#8da798] hover:border-[#c92a2a] hover:text-[#c92a2a]"
                    title={t('presets.delete')}
                    aria-label="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
